import { useMemo } from "react";
import { motion } from "framer-motion";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";
import HeroSlider from "../common/HeroSlider";

// Edit this contact email if needed
const CONTACT_EMAIL = "contact@teardrophydro.co.uk";

export default function PrivacyPolicyPage() {
  usePageMeta({ title: 'Privacy Policy | TearDrop Hydro', description: 'How TearDrop Hydroponics handles your data.', path: '/privacy-policy' });

  // Motion presets
  const fadeUp = useMemo(
    () => ({
      initial: { opacity: 0, y: 12 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.6, ease: "easeOut" },
    }),
    []
  );

  return (
    <Layout>
      {/* HERO */}
      <section className="hero-frame">
        <div className="hero-media">
          <HeroSlider />
          <div className="hero-overlay">
            <h1 className="hero-h1">Privacy Policy</h1>
            <p className="hero-sub">
              How TearDrop Hydroponics collects, uses, and protects your information.
            </p>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section">
        <div className="container">
          <motion.h2 {...fadeUp} className="h2">1) Who we are</motion.h2>
          <p>
            Teardrop Hydroponics (“we”, “us”, “our”) supplies locally grown produce and florals.
            We are the data controller for personal data collected via this website, our storefront,
            and our communications. If you have questions, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
          <p><strong>Last updated:</strong> 17 September 2026</p>
        </div>
      </section>

      {/* WHAT WE COLLECT */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">2) What data we collect</motion.h3>
          <ul>
            <li><strong>Account & Order Data:</strong> name, email, phone, delivery address, order contents, notes, and transaction metadata. Your delivery address is also how we know which streets we serve, and it may be used for occasional printed updates as described in section 5.</li>
            <li><strong>Payment Data:</strong> processed securely by our payment provider (Stripe). We do not store full card details on our servers. For subscription customers, Stripe stores a secure token representing your saved card so that deliveries can be charged automatically at your agreed price.</li>
            <li><strong>Subscription Data:</strong> delivery frequency, product preferences, agreed price per delivery, delivery slot, and subscription status. This data is used to manage and fulfil your recurring delivery service.</li>
            <li><strong>Communications:</strong> emails, messages, and preferences (e.g., newsletter opt-in).</li>
            <li><strong>Device & Usage:</strong> cookie identifiers, IP address, pages viewed, and actions taken (via cookies or analytics).</li>
            <li><strong>Operational Logs:</strong> server logs for security, debugging, and fraud prevention.</li>
          </ul>
        </div>
      </section>

      {/* HOW & WHY */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">3) How and why we use your data</motion.h3>
          <ul>
            <li><strong>To fulfil orders and deliver products</strong> (prepare, package, and deliver your purchases).</li>
            <li><strong>To manage your account</strong> (order history, addresses, preferences).</li>
            <li><strong>To process payments</strong> via our payment provider and prevent fraud.</li>
            <li><strong>To manage and fulfil subscriptions</strong> - including scheduling recurring deliveries and charging your saved payment method before each delivery at the price you have agreed with us. You will receive a receipt by email after every charge. You can pause, skip, or cancel your subscription at any time by contacting us.</li>
            <li><strong>To provide support</strong> and respond to your requests or complaints.</li>
            <li><strong>To improve our website and services</strong> through analytics and performance monitoring.</li>
            <li><strong>To send marketing</strong> by email or text (only with your consent or as permitted by law), and occasionally
              <strong>by post or hand-delivered leaflet</strong> to customers who have ordered from us before. You can opt out of either at any time. See section 5.</li>
            <li><strong>To meet legal obligations</strong> (e.g., tax and accounting requirements).</li>
          </ul>
        </div>
      </section>

      {/* LAWFUL BASES */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">4) Lawful bases (UK GDPR)</motion.h3>
          <ul>
            <li><strong>Contract:</strong> to process and deliver your orders.</li>
            <li><strong>Legitimate interests:</strong> site security, fraud prevention, service improvement, and postal or leaflet marketing to people who have already bought from us (see section 5, where we explain the balancing test we applied and how to object).</li>
            <li><strong>Consent:</strong> email marketing/newsletters and certain analytics or cookies where required.</li>
            <li><strong>Legal obligation:</strong> record keeping for tax and compliance.</li>
          </ul>
        </div>
      </section>

      {/* MARKETING BY POST */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">5) Marketing by post and leaflets</motion.h3>
          <p>
            We are a small local farm, and from time to time we post or hand-deliver something printed
            to people who have bought from us before: a seasonal produce list, a price or delivery update,
            or a note to say we are still growing and delivering nearby. If you have had a leaflet from us,
            this section explains exactly where your address came from and how to stop it.
          </p>
          <ul>
            <li>
              <strong>Where the address came from:</strong> our own order records, and nothing else. It is the delivery
              address you gave us when you placed an order. We do not buy, rent, swap, or scrape address lists,
              we do not obtain addresses from any third party, and we never sell or share your details with other
              businesses for their own marketing.
            </li>
            <li>
              <strong>Who receives it:</strong> only existing customers. We do not leaflet addresses that have never
              ordered from us on the basis of anything we hold about them.
            </li>
            <li>
              <strong>Our lawful basis:</strong> legitimate interests (UK GDPR Article 6(1)(f)). Direct marketing by post
              to existing customers is permitted on this basis. We have weighed our interest in keeping past customers
              informed against your privacy: the mailings are infrequent, they contain only information about the kind
              of produce you already bought from us, they carry no tracking of any kind, and they are delivered by the
              same person who grows and delivers the food. We consider this to be within what a customer of a local
              farm would reasonably expect. The stricter opt-in rules under PECR apply to email, text, and phone
              marketing rather than post, and we follow those separately for electronic messages.
            </li>
            <li>
              <strong>How to stop it:</strong> email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with
              &quot;no post please&quot;, or tell us at the door or in an order note. We will remove your address from
              the mailing list and keep a minimal suppression record so that you are not added back by mistake.
              You do not need to give a reason, there is no time limit, and opting out will never affect your orders,
              your prices, or your deliveries.
            </li>
            <li>
              <strong>Your right to object:</strong> you have an absolute right to object to direct marketing under
              Article 21(2) of the UK GDPR. If you object, we stop. If you would like your data erased more broadly,
              see section 9 on your rights. We may still need to keep basic order records to meet tax and accounting
              obligations, but we can exclude you from all marketing regardless.
            </li>
          </ul>
        </div>
      </section>

      {/* SHARING */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">6) Sharing your data</motion.h3>
          <p>We only share what’s necessary with:</p>
          <ul>
            <li><strong>Payment processors</strong> (e.g., Stripe) to handle transactions and prevent fraud.</li>
            <li><strong>Delivery partners</strong> for local distribution.</li>
            <li><strong>Email & communications providers</strong> (e.g., transactional email/newsletter platforms).</li>
            <li><strong>IT & analytics providers</strong> for hosting, performance, and security.</li>
            <li><strong>Professional advisors</strong> (accountants, legal), and authorities when legally required.</li>
          </ul>
          <p>We do not sell your personal data.</p>
        </div>
      </section>

      {/* INTERNATIONAL TRANSFERS */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">7) International transfers</motion.h3>
          <p>
            If data is transferred outside the UK/EEA (for example, to service providers),
            we rely on appropriate safeguards such as the UK International Data Transfer Agreement,
            Standard Contractual Clauses, or an adequacy decision.
          </p>
        </div>
      </section>

      {/* RETENTION */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">8) Data retention</motion.h3>
          <p>
            We keep personal data only as long as needed for the purposes above. Typical examples:
          </p>
          <ul>
            <li><strong>Order records:</strong> generally up to 6 years to meet accounting/tax obligations.</li>
            <li><strong>Support communications:</strong> retained as needed to manage your requests.</li>
            <li><strong>Marketing preferences:</strong> kept until you opt out or the data is no longer needed.</li>
            <li><strong>Marketing opt-outs:</strong> if you ask us to stop posting or emailing you, we keep a minimal
              suppression record (enough to recognise the address or email) for as long as we do any marketing at all,
              so that you are not contacted again by mistake.</li>
            <li><strong>Analytics data:</strong> retained per provider defaults or as configured for our needs.</li>
          </ul>
        </div>
      </section>

      {/* YOUR RIGHTS */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">9) Your rights (UK GDPR)</motion.h3>
          <ul>
            <li><strong>Access</strong> your personal data.</li>
            <li><strong>Rectify</strong> inaccurate or incomplete data.</li>
            <li><strong>Erase</strong> data in certain circumstances.</li>
            <li><strong>Restrict</strong> or <strong>object</strong> to processing in certain cases.</li>
            <li><strong>Data portability</strong> for data you provided to us where we process by consent or contract.</li>
            <li><strong>Withdraw consent</strong> where processing is based on consent.</li>
            <li><strong>Lodge a complaint</strong> with the UK Information Commissioner’s Office (ICO).</li>
          </ul>
          <p>
            To exercise your rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            We may need to verify your identity before responding.
          </p>
        </div>
      </section>

      {/* COOKIES */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">10) Cookies and analytics</motion.h3>
          <p>
            We may use cookies and similar technologies to run the site, remember preferences,
            measure performance, and improve our services. Where required, we’ll ask for consent.
            You can manage cookies in your browser settings. Disabling some cookies may affect site functionality.
          </p>
        </div>
      </section>

      {/* SECURITY */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">11) Security</motion.h3>
          <p>
            We implement reasonable technical and organisational measures to protect personal data.
            However, no method of transmission or storage is completely secure. If you believe your
            account has been compromised, please contact us immediately.
          </p>
        </div>
      </section>

      {/* CHILDREN */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">12) Children’s privacy</motion.h3>
          <p>
            Our services are not directed to children under 13. We do not knowingly collect personal data from children.
            If you believe a child has provided us personal data, contact us so we can delete it.
          </p>
        </div>
      </section>

      {/* AUTOMATED DECISIONS */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">13) Automated decision-making</motion.h3>
          <p>
            We do not use personal data to make solely automated decisions that have legal or similarly
            significant effects on you.
          </p>
        </div>
      </section>

      {/* CHANGES */}
      <section className="section">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">14) Changes to this policy</motion.h3>
          <p>
            We may update this policy from time to time. We will post the new version on this page
            and update the “Last updated” date above. Significant changes may be communicated by email or site notice.
          </p>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section alt">
        <div className="container">
          <motion.h3 {...fadeUp} className="h3">15) Contact us</motion.h3>
          <p>
            Questions, requests, or complaints about this policy or your personal data? Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or use the contact options on our website.
          </p>
        </div>
      </section>
    </Layout>
  );
}
